import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { AnimatePresence, motion } from 'framer-motion'
import { getFlavor, type Flavor } from './data'
import FlavorGrid from './components/FlavorGrid'
import FlavorDetail from './components/FlavorDetail'
import PairDetail from './components/PairDetail'
import './FlavorsPage.css'

/**
 * «Вкусовой конструктор» — раздел на основе графа сочетаний из книги «Тезаурус вкусов».
 * Экраны в одной странице, состояние — в URL, чтобы работали «Назад» и перезагрузка:
 *   1) сетка вкусов по категориям (без параметров);
 *   2) экран вкуса (?flavor=): hero + сочетания по уровням;
 *   3) экран пары (?flavor=&pair=): A + B, уровень и описание сочетания.
 */
type GridView = 'table' | 'list'

const FlavorsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState('')
  const [view, setView] = useState<GridView>('table')

  const flavorSlug = searchParams.get('flavor') ?? ''
  const pairSlug = searchParams.get('pair') ?? ''

  // Открывая экран вкуса/пары (в т.ч. переход по сочетанию), прокручиваем страницу
  // наверх — иначе она открывается на позиции скролла предыдущего экрана.
  useEffect(() => {
    if (flavorSlug) window.scrollTo(0, 0)
  }, [flavorSlug, pairSlug])

  // Валидируем slug из URL: неизвестный вкус → возврат к сетке.
  const flavor = useMemo<Flavor | undefined>(
    () => (flavorSlug ? getFlavor(flavorSlug) : undefined),
    [flavorSlug],
  )

  const openFlavor = useCallback(
    (f: Flavor) => setSearchParams({ flavor: f.slug }),
    [setSearchParams],
  )

  // Клик по сочетанию на экране вкуса → экран пары (текущий вкус × slug).
  const openPair = useCallback(
    (slug: string) => setSearchParams({ flavor: flavorSlug, pair: slug }),
    [setSearchParams, flavorSlug],
  )

  const backToGrid = useCallback(
    () => setSearchParams({}),
    [setSearchParams],
  )

  const backToFlavor = useCallback(
    () => setSearchParams({ flavor: flavorSlug }),
    [setSearchParams, flavorSlug],
  )

  // Экран пары (A + B)
  if (flavor && pairSlug) {
    return (
      <div className="flavors-page">
        <PairDetail
          flavor={flavor}
          pairSlug={pairSlug}
          onBack={backToFlavor}
          onOpenPair={openPair}
        />
      </div>
    )
  }

  // Экран вкуса
  if (flavor) {
    return (
      <div className="flavors-page">
        <FlavorDetail
          flavor={flavor}
          onBack={backToGrid}
          onOpen={openPair}
        />
      </div>
    )
  }

  // Сетка вкусов
  return (
    <div className="flavors-page">
      <header className="flavors-header">
        <h1 className="flavors-title">Вкусовой конструктор</h1>
        <p className="flavors-lead">
          Выберите вкус — и посмотрите, с чем он сочетается.
        </p>
      </header>

      <div className="flavors-search">
        <input
          type="text"
          className="flavors-search__input"
          placeholder="Найти вкус"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search ? (
          <button
            type="button"
            className="flavors-search__clear"
            onClick={() => setSearch('')}
            aria-label="Очистить"
          >
            <Icon icon="solar:close-circle-linear" width={24} height={24} />
          </button>
        ) : (
          <svg
            className="flavors-search__icon"
            width={24}
            height={24}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M9.5 16C7.68333 16 6.146 15.3707 4.888 14.112C3.63 12.8533 3.00067 11.316 3 9.5C2.99933 7.684 3.62867 6.14667 4.888 4.888C6.14733 3.62933 7.68467 3 9.5 3C11.3153 3 12.853 3.62933 14.113 4.888C15.373 6.14667 16.002 7.684 16 9.5C16 10.2333 15.8833 10.925 15.65 11.575C15.4167 12.225 15.1 12.8 14.7 13.3L20.3 18.9C20.4833 19.0833 20.575 19.3167 20.575 19.6C20.575 19.8833 20.4833 20.1167 20.3 20.3C20.1167 20.4833 19.8833 20.575 19.6 20.575C19.3167 20.575 19.0833 20.4833 18.9 20.3L13.3 14.7C12.8 15.1 12.225 15.4167 11.575 15.65C10.925 15.8833 10.2333 16 9.5 16ZM9.5 14C10.75 14 11.8127 13.5627 12.688 12.688C13.5633 11.8133 14.0007 10.7507 14 9.5C13.9993 8.24933 13.562 7.187 12.688 6.313C11.814 5.439 10.7513 5.00133 9.5 5C8.24867 4.99867 7.18633 5.43633 6.313 6.313C5.43967 7.18967 5.002 8.252 5 9.5C4.998 10.748 5.43567 11.8107 6.313 12.688C7.19033 13.5653 8.25267 14.0027 9.5 14Z"
              fill="currentColor"
            />
          </svg>
        )}
      </div>

      <div className="flavors-tabs" role="tablist" aria-label="Вид">
        {(
          [
            { key: 'table', label: 'Таблица' },
            { key: 'list', label: 'Список' },
          ] as const
        ).map(tab => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={view === tab.key}
            className={`flavors-tabs__btn ${view === tab.key ? 'active' : ''}`}
            onClick={() => setView(tab.key)}
          >
            {view === tab.key && (
              <motion.span
                layoutId="flavors-tab-pill"
                className="flavors-tabs__pill"
                transition={{ type: 'spring', stiffness: 420, damping: 34 }}
              />
            )}
            <span className="flavors-tabs__label">{tab.label}</span>
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={view}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
        >
          <FlavorGrid search={search} view={view} onSelect={openFlavor} />
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

export default FlavorsPage
